// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios from 'axios';

import { Vendor, VendorDTO, VendorUserDTO, VendorUser, VendorUsersDTO } from '@shared/models/vendor';
import BaseService from '@core/services/base';
import { ListRequestParams } from '@shared/types/services';
import { REQUEST_PARAMS } from '@shared/constants/services';
import { VENDOR_FIELDS } from '@shared/constants/vendor';
import { Option } from '@shared/components/Select';

@injectable()
export default class VendorsService extends BaseService<VendorDTO, Vendor> {
  static diToken = Symbol('vendors-service');
  static testVendors = false;

  constructor() {
    super({ domainArea: 'vendors', Model: Vendor });
  }
  
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        vendors(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          sort: { field: ${VENDOR_FIELDS.name}, type: ${sorting?.type}},
        ) {
          items {
            ${VENDOR_FIELDS.id}
            ${VENDOR_FIELDS.name}
            ${VENDOR_FIELDS.types}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Get a list of users with a given role (eg. Pilot, Processing tech, ...).
  async getCompanyUsers(params: { roleName: string, onlyActive: boolean }): Promise<Array<VendorUser>> {
    const { data } = await Axios.get<Array<VendorUserDTO>>(this.getURL('users'), { params });

    return data.map((user) => new VendorUser(user));
  }

  // Get users for a set of roles.  Response is a dictionary where key is roleName and value is list of users.
  async getCompanyUsersByRoles(rolesRequest: { roleName: string, onlyActive: boolean }[]): Promise<{ [role: string]: Option[]; }> {
    let usersByRoles = {};

    if (VendorsService.testVendors == false) {
      //const { data } = await Axios.post<{ [roleName: string]: Array<VendorUser> }>(this.getURL('usersInRoles'), { rolesRequest }); 
      const { data } = await Axios.post(this.getURL('usersInRoles'), { rolesRequest }); 
      usersByRoles = Object.assign({}, ...data.map((x) => ({[x.roleName] : 
        x.usersInRole.map(u => ({'id': u.id, 'label': u.fullName}))})))
    }
    else {
      usersByRoles = {
        "Pilot": [{id: '1', label: 'one'}, {id: '2', label: 'two'},],
        "Observer": [{id: '3', label: 'ba'}, {id: '4', label: 'bon'},],
      };
    }

    return usersByRoles;
  }

  // Get a list of vendor users (include only these roles: observer, pilot, post-tech, production-tech).
  async getVendorStaff(params: { onlyActive: boolean }): Promise<Array<VendorUsersDTO>> {
    const { data } = await Axios.get<Array<VendorUsersDTO>>(this.getURL('staff'), { params });

    //console.log("*** getUsersAllRoles Data:", data);

    return data;
  }

  async getList(params: ListRequestParams) {
    if (VendorsService.testVendors == false)
    {
      try {
        const query1 = this.getListQuery(params);
        const { data } = await Axios.get('/vendors/index', {
          params: { query: query1 },
        });
        return {
          pageInfo: data.vendors.pageInfo,
          items: data.vendors.items
        };
      }
      catch(err)
      {
        throw err;
      }
    }
    {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const instance = Axios.create();
      instance.defaults.baseURL = window.location.origin;
      const { data } = await instance.get('mocks/vendors.json');
      return data;
    }
  }

  // TODO remove
  //async get(id: VendorDTO['id']) {
  //  await new Promise((resolve) => setTimeout(resolve, 1000));

  //  const instance = Axios.create();
    
  //  instance.defaults.baseURL = window.location.origin;

  //  const { data } = await instance.get('mocks/vendor-details.json');

  //  return new Vendor(data.data);
  //}

}
